import {Message} from '@arco-design/web-vue'
import logger from './util/logger.js'
import {findDisplayCurrency} from './constants/displayCurrencies.js'
import {
    addDecimals,
    multiplyDecimals,
    formatDecimal,
    isPositiveDecimal,
    formatFiat,
    formatFiatAdaptive,
    formatFiatProduct,
    formatFiatSum
} from './util/decimal.mjs'

const unit = {
    'zh_CN': ['亿', '万'],
    'zh_Hant': ['亿', '万'],
    'en-US': ['HM', 'TH'],
    'ja': ['億', '万'],
    'ko': ['亿', '만']
};

export default {
    addDecimals,
    multiplyDecimals,
    formatDecimal,
    isPositiveDecimal,
    formatFiat,
    formatFiatAdaptive,
    formatFiatProduct,
    formatFiatSum,
    /**
     * 获取多语言文本
     * @param {string} key - 语言键值
     * @param {string} defaultText - 默认文本（英文）
     * @returns {string} 对应语言的文本
     */
    getI18nText(key, defaultText = '') {
        const lang = this.getCache('lang', 'en-US');
        
        // 简单的多语言映射，这里只处理常用的文字
        const translations = {
            // 登录相关
            'login_success': {
                'zh_CN': '登录成功',
                'zh_Hant': '登錄成功',
                'ja': 'ログイン成功',
                'ko': '로그인 성공',
                'en-US': 'Login successful',
                'gm': 'Anmeldung erfolgreich',
                'ru': 'Вход выполнен успешно',
                'th': 'เข้าสู่ระบบสำเร็จ',
                'au': 'Login successful'
            },
            'login_success_message': {
                'zh_CN': '登录成功！正在跳转到资产页面...',
                'zh_Hant': '登錄成功！正在跳轉到資產頁面...',
                'ja': 'ログイン成功！アセットページにリダイレクトしています...',
                'ko': '로그인 성공! 자산 페이지로 이동 중...',
                'en-US': 'Login successful! Redirecting to assets page...',
                'gm': 'Anmeldung erfolgreich! Weiterleitung zur Vermögensseite...',
                'ru': 'Вход выполнен успешно! Перенаправление на страницу активов...',
                'th': 'เข้าสู่ระบบสำเร็จ! กำลังเปลี่ยนเส้นทางไปยังหน้าสินทรัพย์...',
                'au': 'Login successful! Redirecting to assets page...'
            },
            'login_welcome_back': {
                'zh_CN': '欢迎回来，祝您交易愉快！',
                'zh_Hant': '歡迎回來，祝您交易愉快！',
                'ja': 'おかえりなさい、取引をお楽しみください！',
                'ko': '돌아오신 것을 환영합니다. 즐거운 거래 되세요!',
                'en-US': 'Welcome back, happy trading!',
                'gm': 'Willkommen zurück, frohes Trading!',
                'ru': 'Добро пожаловать обратно, удачной торговли!',
                'th': 'ยินดีต้อนรับกลับมา ขอให้การเทรดสนุก!',
                'au': 'Welcome back, happy trading!'
            },
            'login_failed': {
                'zh_CN': '登录失败',
                'zh_Hant': '登錄失敗',
                'ja': 'ログイン失敗',
                'ko': '로그인 실패',
                'en-US': 'Login failed',
                'gm': 'Anmeldung fehlgeschlagen',
                'ru': 'Ошибка входа',
                'th': 'เข้าสู่ระบบล้มเหลว',
                'au': 'Login failed'
            },
            'login_failed_check_credentials': {
                'zh_CN': '登录失败，请检查账号密码',
                'zh_Hant': '登錄失敗，請檢查帳號密碼',
                'ja': 'ログインに失敗しました。アカウントとパスワードを確認してください',
                'ko': '로그인 실패, 계정과 비밀번호를 확인해주세요',
                'en-US': 'Login failed, please check your account and password',
                'gm': 'Anmeldung fehlgeschlagen, bitte überprüfen Sie Ihr Konto und Passwort',
                'ru': 'Ошибка входа, проверьте аккаунт и пароль',
                'th': 'เข้าสู่ระบบล้มเหลว กรุณาตรวจสอบบัญชีและรหัสผ่าน',
                'au': 'Login failed, please check your account and password'
            },
            'login_confirm_credentials': {
                'zh_CN': '请确认邮箱和密码是否正确',
                'zh_Hant': '請確認郵箱和密碼是否正確',
                'ja': 'メールアドレスとパスワードが正しいか確認してください',
                'ko': '이메일과 비밀번호가 올바른지 확인해주세요',
                'en-US': 'Please confirm if your email and password are correct',
                'gm': 'Bitte bestätigen Sie, ob Ihre E-Mail und Ihr Passwort korrekt sind',
                'ru': 'Пожалуйста, подтвердите правильность email и пароля',
                'th': 'กรุณายืนยันว่าอีเมลและรหัสผ่านของคุณถูกต้อง',
                'au': 'Please confirm if your email and password are correct'
            },
            'retry': {
                'zh_CN': '重试',
                'zh_Hant': '重試',
                'ja': '再試行',
                'ko': '재시도',
                'en-US': 'Retry',
                'gm': 'Wiederholen',
                'ru': 'Повторить',
                'th': 'ลองใหม่',
                'au': 'Retry'
            },
            'confirm': {
                'zh_CN': '确定',
                'zh_Hant': '確定',
                'ja': '確認',
                'ko': '확인',
                'en-US': 'Confirm',
                'gm': 'Bestätigen',
                'ru': 'Подтвердить',
                'th': 'ยืนยัน',
                'au': 'Confirm'
            },
            're_login': {
                'zh_CN': '重新登录',
                'zh_Hant': '重新登錄',
                'ja': '再ログイン',
                'ko': '재로그인',
                'en-US': 'Re-login',
                'gm': 'Erneut anmelden',
                'ru': 'Повторный вход',
                'th': 'เข้าสู่ระบบใหม่',
                'au': 'Re-login'
            },
            'select_box_first': {
                'zh_CN': '请先选择盲盒',
                'zh_Hant': '請先選擇盲盒',
                'ja': '最初にブラインドボックスを選択してください',
                'ko': '먼저 블라인드 박스를 선택해주세요',
                'en-US': 'Please select a box first'
            },
            'operation_failed_retry': {
                'zh_CN': '操作失败，请重试',
                'zh_Hant': '操作失敗，請重試',
                'ja': '操作に失敗しました。再試行してください',
                'ko': '작업 실패, 다시 시도해주세요',
                'en-US': 'Operation failed, please try again'
            },
            'deposit_application_success': {
                'zh_CN': '充值申请提交成功，请等待审核',
                'zh_Hant': '充值申請提交成功，請等待審核',
                'ja': '入金申請が正常に送信されました。審査をお待ちください',
                'ko': '입금 신청이 성공적으로 제출되었습니다. 심사를 기다려주세요',
                'en-US': 'Deposit application submitted successfully, please wait for review'
            },
            'submit_failed_retry': {
                'zh_CN': '提交失败，请重试',
                'zh_Hant': '提交失敗，請重試',
                'ja': '送信に失敗しました。再試行してください',
                'ko': '제출 실패, 다시 시도해주세요',
                'en-US': 'Submit failed, please try again'
            },
            'language_setting_saved': {
                'zh_CN': '语言设置已保存',
                'zh_Hant': '語言設置已保存',
                'ja': '言語設定が保存されました',
                'ko': '언어 설정이 저장되었습니다',
                'en-US': 'Language setting saved'
            },
            'currency_setting_saved': {
                'zh_CN': '货币设置已保存',
                'zh_Hant': '貨幣設置已保存',
                'ja': '通貨設定が保存されました',
                'ko': '통화 설정이 저장되었습니다',
                'en-US': 'Currency setting saved'
            },
            'setting_saved': {
                'zh_CN': '设置已保存',
                'zh_Hant': '設置已保存',
                'ja': '設定が保存されました',
                'ko': '설정이 저장되었습니다',
                'en-US': 'Settings saved'
            },
            'fill_complete_deposit_info': {
                'zh_CN': '请填写完整的充值信息',
                'zh_Hant': '請填寫完整的充值信息',
                'ja': '完全な入金情報を入力してください',
                'ko': '완전한 입금 정보를 입력해주세요',
                'en-US': 'Please fill in complete deposit information'
            }
        };
        
        if (translations[key] && translations[key][lang]) {
            return translations[key][lang];
        }
        
        return defaultText || key;
    },
    thousandSeparator(value, decimal = 2) {
        if (!value || value == '--' || isNaN(value)) return '0.00';
        if (parseFloat(value) === 0) {
            return '0.00';
        }
        const number = parseFloat(value);
        const formattedNumber = number.toFixed(decimal);

        const parts = formattedNumber.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        return parts.join('.');
    },
    thousandUnit(value, decimal = 2) {
        if (!value) return '0';
        const number = parseFloat(value);
        if (number >= 10000) {
            return (number / 10000).toFixed(2) + 'M';
        }
        if (number >= 1000) {
            return (number / 1000).toFixed(2) + 'K';
        }
        return number.toFixed(2)
    },
    float(v) {
        //不指定数字显示精度，格式化返回数字显示
        if (typeof v == 'undefined' || Number.isNaN(v)) {
            return '0.00';
        }
        v = String(v).replace(",", "");
        if (parseFloat(v) == 0) {
            return '0.00';
        }
        const _fv = parseFloat(v) % 1 === 0 ? parseFloat(v).toFixed(2) : String(parseFloat(v));
        const parts = _fv.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        return parts.join('.');
    },
    Number2(value) {
        try {
            return parseFloat(value).toFixed(2);
        } catch (e) {
            return '0.00';
        }
    },
    bigNumberToString(value) {
        value = parseFloat(value);
        let lang = this.getCache('lang');
        if (lang == '') {
            lang = 'en-US'
        }
        if (value >= 100000000) {
            return (value / 100000000).toFixed(2) + unit[lang][0];
        }
        if (value >= 10000) {
            return (value / 10000).toFixed(2) + unit[lang][1];
        }
        return parseFloat(value).toFixed(2);
    },
    notOpen() {
        // 根据当前语言显示相应提示
        const lang = this.getCache('lang', 'en-US');
        let message = 'Under development, not yet available';
        
        if (lang === 'zh_CN') {
            message = '开发中，暂未开放';
        } else if (lang === 'zh_Hant') {
            message = '開發中，暫未開放';
        } else if (lang === 'ja') {
            message = '開発中、まだ利用できません';
        } else if (lang === 'ko') {
            message = '개발 중, 아직 사용할 수 없습니다';
        }
        
        Message.info({
            content: message,
            // icon: ''
        });
        
        // 使用 logger 记录日志
        logger.info('功能未开放提示:', message);
    },
    sortDesc(arrayList) {
        const result = [...arrayList].sort((a, b) => b.change_rate - a.change_rate)
        return result;
    },
    sortAsc(arrayList) {
        const result = [...arrayList].sort((a, b) => a.change_rate - b.change_rate)
        return result;
    },
    sortQuan(arrayList) {
        const result = [...arrayList].sort((a, b) => (b.quantity * b.new_price) - (a.quantity * a.new_price))
        return result;
    },
    replaceEmpty(v) {
        return v.replace(" ", "").replace("+", "");
    },
    isEmailAccount(input) {
        var regex = /@/;
        return regex.test(input);
    },
    /**
     * @desc 日期时间格式化
     * @param date 需要格式化的日期
     * @param format 格式化字符串(y-m-d h:i:s)
     * @param type  date的格式类型：1-日期字符串(2017/12/04 12:12:12) 2-时间戳(1603676514690) 3-日期字符串，无连接符(20171204121212)
     * 4-new Date()时间格式(Thu Oct 01 2020 00:00:00 GMT+0800 (中国标准时间))
     * @param isMs  时间戳精度是否为毫秒，默认为true（当精度为秒时传false），type=2时有效
     **/
    dateFormatter(date, format, type = 1, isMs = true) {
        let formatDate = ""
        if (type === 3) {
            formatDate = this._formatTimeStr(date, format)
        } else {
            formatDate = this._formatDate(format, date, type, isMs)
        }
        return formatDate;
    },
    _formatDate(formatStr, fdate, type = 1, isMs) {
        if (!fdate) return '';
        let fTime, fStr = 'ymdhis';
        if (type === 4) {
            fTime = fdate;
        } else {
            fdate = fdate.toString()
            if (~fdate.indexOf('.')) {
                fdate = fdate.substring(0, fdate.indexOf('.'));
            }
            fdate = fdate.replace('T', ' ').replace(/\-/g, '/');
            if (!formatStr)
                formatStr = "y-m-d h:i:s";
            if (fdate) {
                if (type === 2) {
                    fdate = isMs ? Number(fdate) : Number(fdate) * 1000
                }
                fTime = new Date(fdate);
            } else {
                fTime = new Date();
            }
        }
        let month = fTime.getMonth() + 1;
        let day = fTime.getDate();
        let hours = fTime.getHours();
        let minu = fTime.getMinutes();
        let second = fTime.getSeconds();
        month = month < 10 ? '0' + month : month;
        day = day < 10 ? '0' + day : day;
        hours = hours < 10 ? ('0' + hours) : hours;
        minu = minu < 10 ? '0' + minu : minu;
        second = second < 10 ? '0' + second : second;
        let formatArr = [
            fTime.getFullYear().toString(),
            month.toString(),
            day.toString(),
            hours.toString(),
            minu.toString(),
            second.toString()
        ]
        for (let i = 0; i < formatArr.length; i++) {
            formatStr = formatStr.replace(fStr.charAt(i), formatArr[i]);
        }
        return formatStr;
    },
    str2number(num) {
        if (num && num != undefined && num != null) {
            let _num = num;
            _num = _num.toString();
            _num = _num.replace(/,/gi, '');
            return _num
        } else {
            return num
        }
    },
    /**
     * 获取计价货币符号
     * @returns {any|string}
     */
    getLCS() {
        let currency = this.getCache('local-currency');
        if (typeof currency === 'string' && currency !== '') {
            try {
                currency = JSON.parse(currency);
            } catch (error) {
                return '$';
            }
        }
        const supportedCurrency = findDisplayCurrency(currency);
        return supportedCurrency ? supportedCurrency.symbol : '$';
    },
    /**
     * 获取计价汇率
     * @returns {number}
     */
    getLCR() {
        let currency = this.getCache('local-currency');
        if (typeof currency === 'string' && currency !== '') {
            try {
                currency = JSON.parse(currency);
            } catch (error) {
                return '1.00';
            }
        }
        const rate = this.getCache('local-fee');
        if (findDisplayCurrency(currency) && typeof rate !== 'undefined' && isPositiveDecimal(rate)) {
            return String(rate);
        } else {
            return '1.00';
        }
    },
    getDateStr() {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    },
    getDateTimeStr() {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const h = String(date.getHours()).padStart(2,'0');
        const minut = String(date.getMinutes()).padStart(2,'0');
        const seconds = String(date.getSeconds()).padStart(2,'0');
        return `${year}-${month}-${day} ${h}:${minut}:${seconds}`;
    },
    getDateAddDay(date, n) {
        let diyDate = new Date(date)
        date = new Date(diyDate.setDate(diyDate.getDate() + n));
        let year = date.getFullYear();
        let month = String(date.getMonth() + 1).padStart(2, '0');
        let day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    },
    getCurrentTicker(id, ticker) {
        for (var c in ticker) {
            if (parseInt(id) === parseInt(c)) {
                return ticker[c];
            }
        }
        return {};
    },
    compareCurrency(list, ticker) {
        for (var c in ticker) {
            var _ticker = ticker[c];
            var index = list.findIndex(item => parseInt(item.id) === parseInt(c));
            if (index >= 0) {
                list[index]['new_price'] = _ticker['c'];
                list[index]['change_rate'] = _ticker['P'];
                list[index]['quantity'] = _ticker['v'];
            }
        }
        return list;
    },
    hideText(str) {
        if (str.length <= 8) {
            return str;
        }

        const start = str.substring(0, 4);
        const end = str.substring(str.length - 8);

        return start + "****" + end;
    },
    setCache(k, v) {
        return localStorage.setItem(k, v)
    },
    getCache(k, def = '') {
        let v = localStorage.getItem(k);
        return v ? v : def;
    },
    delCache(k) {
        return localStorage.removeItem(k)
    },
    validatePhoneNumber(phoneNumber) {
        // 全球手机号码的正则表达式
        const regex = /^[+]\d{1,3}[-\s]?\d{1,14}$/;
        return regex.test(phoneNumber);
    },
    validateEmail(email) {
        // 电子邮件地址的正则表达式
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email)
    },
    
    // ========== 币种过滤方法 ==========
    
    /**
     * 过滤可提现的币种
     * @param {Array} currencies - 币种列表
     * @returns {Array} 过滤后的可提现币种列表
     */
    filterWithdrawableCurrencies(currencies) {
        if (!Array.isArray(currencies)) {
            console.warn('⚠️ filterWithdrawableCurrencies: 输入不是数组');
            return [];
        }
        
        return currencies.filter(currency => {
            const canWithdraw = currency.is_withdraw === 1 || currency.is_withdraw === '1';
            console.log(`💰 币种 ${currency.symbol || currency.name}: is_withdraw = ${currency.is_withdraw}, 可提现: ${canWithdraw}`);
            return canWithdraw;
        });
    },
    
    /**
     * 过滤可充值的币种
     * @param {Array} currencies - 币种列表
     * @returns {Array} 过滤后的可充值币种列表
     */
    filterRechargeCurrencies(currencies) {
        if (!Array.isArray(currencies)) {
            console.warn('⚠️ filterRechargeCurrencies: 输入不是数组');
            return [];
        }
        
        return currencies.filter(currency => {
            const canRecharge = currency.is_recharge === 1 || currency.is_recharge === '1';
            console.log(`💰 币种 ${currency.symbol || currency.name}: is_recharge = ${currency.is_recharge}, 可充值: ${canRecharge}`);
            return canRecharge;
        });
    },
    
    // ========== 首页币种排序方法 ==========
    
    /**
     * 按涨跌幅降序排列（涨幅榜）
     */
    sortDesc(list) {
        if (!Array.isArray(list)) return [];
        return [...list].sort((a, b) => {
            const rateA = parseFloat(a.change_rate) || 0;
            const rateB = parseFloat(b.change_rate) || 0;
            return rateB - rateA; // 降序
        });
    },
    
    /**
     * 按涨跌幅升序排列（跌幅榜）
     */
    sortAsc(list) {
        if (!Array.isArray(list)) return [];
        return [...list].sort((a, b) => {
            const rateA = parseFloat(a.change_rate) || 0;
            const rateB = parseFloat(b.change_rate) || 0;
            return rateA - rateB; // 升序
        });
    },
    
    /**
     * 按成交额降序排列
     */
    sortQuan(list) {
        if (!Array.isArray(list)) return [];
        return [...list].sort((a, b) => {
            const quotaA = parseFloat(a.quota) || 0;
            const quotaB = parseFloat(b.quota) || 0;
            return quotaB - quotaA; // 降序
        });
    },
    
    /**
     * 格式化大数字为字符串（带单位）
     */
    bigNumberToString(value) {
        if (!value || isNaN(value)) return '0';
        const num = parseFloat(value);
        
        if (num >= 1e9) {
            return (num / 1e9).toFixed(2) + 'B';
        } else if (num >= 1e6) {
            return (num / 1e6).toFixed(2) + 'M';
        } else if (num >= 1e3) {
            return (num / 1e3).toFixed(2) + 'K';
        } else {
            return num.toFixed(2);
        }
    },
    
    /**
     * 复制文本到剪贴板（兼容性处理）
     * @param {string} text - 要复制的文本
     * @param {function} successCallback - 成功回调
     * @param {function} errorCallback - 错误回调
     */
    copyToClipboard(text, successCallback, errorCallback) {
        if (!text) {
            if (errorCallback) {
                const lang = this.getCache('lang', 'en-US');
                let message = 'No content to copy';
                if (lang === 'zh_CN' || lang === 'zh' || lang === 'zh-CN') {
                    message = '没有可复制的内容';
                } else if (lang === 'zh-Hant') {
                    message = '沒有可複製的內容';
                } else if (lang === 'ja') {
                    message = 'コピーする内容がありません';
                } else if (lang === 'ko') {
                    message = '복사할 내용이 없습니다';
                }
                errorCallback(message);
            }
            return;
        }
        
        // 检查是否支持现代Clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(() => {
                if (successCallback) {
                    const lang = this.getCache('lang', 'en-US');
                    let message = 'Copy successful';
                    if (lang === 'zh_CN' || lang === 'zh' || lang === 'zh-CN') {
                        message = '复制成功';
                    } else if (lang === 'zh-Hant') {
                        message = '複製成功';
                    } else if (lang === 'ja') {
                        message = 'コピー成功';
                    } else if (lang === 'ko') {
                        message = '복사 성공';
                    }
                    successCallback(message);
                }
            }).catch((err) => {
                console.error('Clipboard API失败:', err);
                this.fallbackCopyText(text, successCallback, errorCallback);
            });
        } else {
            // 使用备用方案
            this.fallbackCopyText(text, successCallback, errorCallback);
        }
    },
    
    /**
     * 备用复制方案
     * @param {string} text - 要复制的文本
     * @param {function} successCallback - 成功回调
     * @param {function} errorCallback - 错误回调
     */
    fallbackCopyText(text, successCallback, errorCallback) {
        try {
            // 创建临时文本区域
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            
            // 选择文本并复制
            textArea.focus();
            textArea.select();
            const successful = document.execCommand('copy');
            
            // 清理临时元素
            document.body.removeChild(textArea);
            
            const lang = this.getCache('lang', 'en-US');
            
            if (successful) {
                if (successCallback) {
                    let message = 'Copy successful';
                    if (lang === 'zh_CN' || lang === 'zh' || lang === 'zh-CN') {
                        message = '复制成功';
                    } else if (lang === 'zh-Hant') {
                        message = '複製成功';
                    } else if (lang === 'ja') {
                        message = 'コピー成功';
                    } else if (lang === 'ko') {
                        message = '복사 성공';
                    }
                    successCallback(message);
                }
            } else {
                if (errorCallback) {
                    let message = 'Copy failed, please copy manually';
                    if (lang === 'zh_CN' || lang === 'zh' || lang === 'zh-CN') {
                        message = '复制失败，请手动复制';
                    } else if (lang === 'zh-Hant') {
                        message = '複製失敗，請手動複製';
                    } else if (lang === 'ja') {
                        message = 'コピーに失敗しました。手動でコピーしてください';
                    } else if (lang === 'ko') {
                        message = '복사 실패, 수동으로 복사해주세요';
                    }
                    errorCallback(message);
                }
            }
        } catch (err) {
            console.error('备用复制方案失败:', err);
            if (errorCallback) {
                const lang = this.getCache('lang', 'en-US');
                let message = 'Copy failed, please copy manually';
                if (lang === 'zh_CN' || lang === 'zh' || lang === 'zh-CN') {
                    message = '复制失败，请手动复制';
                } else if (lang === 'zh-Hant') {
                    message = '複製失敗，請手動複製';
                } else if (lang === 'ja') {
                    message = 'コピーに失敗しました。手動でコピーしてください';
                } else if (lang === 'ko') {
                    message = '복사 실패, 수동으로 복사해주세요';
                }
                errorCallback(message);
            }
        }
    }
}
